import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';

/**
 * Support tickets service — helpdesk natif (`/me/support/*`).
 *
 * Deux scopes :
 *  - `vendor_business`   : tickets internes du vendeur (son business)
 *  - `platform_support`  : tickets vers l'équipe support vendeur.site
 *    (synchronisés vers Odoo Helpdesk — cf. `odoo_sync_status`)
 *
 * + grants d'accès support : le vendeur autorise (temporairement, révocable)
 * un agent support à consulter son espace business.
 */

export type TicketScope = 'vendor_business' | 'platform_support';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_customer'
  | 'resolved'
  | 'closed';

export interface SupportTicket {
  id: number;
  scope: TicketScope;
  source: string;
  priority: TicketPriority;
  status: TicketStatus;
  subject: string;
  description?: string | null;
  linked_kind?: string | null;
  linked_id?: number | null;
  attached_ticket_id?: number | null;
  odoo_ticket_id?: number | null;
  odoo_sync_status?: string | null;
  assignee_user_id?: number | null;
  assignee_ai_agent_id?: number | null;
  task_id?: number | null;
  subscribers?: number[];
  pending_approvals?: Array<{
    id: number;
    title?: string;
    subject?: string;
    body?: string;
    requested_by_agent_name?: string;
  }>;
  meta?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface AssigneeHuman {
  id: number;
  name: string;
  email?: string | null;
  is_owner: boolean;
}

export interface AssigneeAiAgent {
  id: number;
  name: string;
  specialty?: string | null;
  avatar_emoji?: string | null;
  color?: string | null;
  lanci_workspace_id?: number | null;
}

export interface TicketTimesheet {
  id: number;
  ticket_id: number;
  agent_user_id: number;
  agent_role: string;
  minutes: number;
  billable: boolean;
  note?: string | null;
  work_date?: string | null;
  created_at: string;
}

export interface SupportKpis {
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_scope: Record<string, number>;
  open_total: number;
  created_30d: number;
  resolved_30d: number;
  avg_first_response_h: number | null;
  avg_resolution_h: number | null;
  timesheet_total_minutes: number;
  timesheet_billable_minutes: number;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  author_user_id?: number | null;
  author_role: string;
  kind?: string | null;
  internal: boolean;
  body: string;
  created_at: string;
}

export interface SupportGrant {
  id: number;
  ticket_id?: number | null;
  agent_email?: string | null;
  scope_modules?: string[] | null;
  status: string;
  reason?: string | null;
  active: boolean;
  requested_at?: string | null;
  granted_at?: string | null;
  expires_at?: string | null;
}

export interface SupportSummary {
  open: number;
  in_progress: number;
  waiting_customer: number;
  resolved_7d: number;
  total: number;
}

export interface CreateTicketPayload {
  scope: TicketScope;
  subject: string;
  description?: string;
  priority?: TicketPriority;
  source?: string;
  linked_kind?: string;
  linked_id?: number;
}

@Injectable({ providedIn: 'root' })
export class SupportTicketsService {
  private readonly base: string;

  constructor(private http: HttpClient, config: ConfigService) {
    this.base = `${config.apiUrl}/me/support`;
  }

  // ── Tickets ─────────────────────────────────────────────────────
  createTicket(payload: CreateTicketPayload): Observable<{ ticket: SupportTicket }> {
    return this.http.post<{ ticket: SupportTicket }>(`${this.base}/tickets`, payload);
  }

  listTickets(opts?: {
    scope?: TicketScope;
    status?: TicketStatus | string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Observable<{ items: SupportTicket[]; total: number }> {
    const params: Record<string, string> = {};
    if (opts?.scope) params['scope'] = opts.scope;
    if (opts?.status) params['status'] = opts.status;
    if (opts?.q) params['q'] = opts.q;
    if (opts?.limit != null) params['limit'] = String(opts.limit);
    if (opts?.offset != null) params['offset'] = String(opts.offset);
    return this.http.get<{ items: SupportTicket[]; total: number }>(
      `${this.base}/tickets`, { params },
    );
  }

  getTicket(id: number): Observable<{ ticket: SupportTicket; messages: TicketMessage[] }> {
    return this.http.get<{ ticket: SupportTicket; messages: TicketMessage[] }>(
      `${this.base}/tickets/${id}`,
    );
  }

  postMessage(
    ticketId: number,
    body: string,
    internal = false,
  ): Observable<{ message: TicketMessage }> {
    return this.http.post<{ message: TicketMessage }>(
      `${this.base}/tickets/${ticketId}/messages`, { body, internal },
    );
  }

  updateTicket(
    id: number,
    payload: { status?: TicketStatus; priority?: TicketPriority },
  ): Observable<{ ticket: SupportTicket }> {
    return this.http.patch<{ ticket: SupportTicket }>(
      `${this.base}/tickets/${id}`, payload,
    );
  }

  attachTicket(
    id: number,
    attachedTicketId: number,
  ): Observable<{ ticket: SupportTicket }> {
    return this.http.post<{ ticket: SupportTicket }>(
      `${this.base}/tickets/${id}/attach`, { attached_ticket_id: attachedTicketId },
    );
  }

  getSummary(): Observable<SupportSummary> {
    return this.http.get<SupportSummary>(`${this.base}/summary`);
  }

  // ── Assignation / abonnés / kanban-task / timesheets / KPIs ───────
  listAssignees(): Observable<{ humans: AssigneeHuman[]; ai_agents: AssigneeAiAgent[] }> {
    return this.http.get<{ humans: AssigneeHuman[]; ai_agents: AssigneeAiAgent[] }>(
      `${this.base}/assignees`,
    );
  }

  assignTicket(
    id: number,
    payload: {
      assignee_user_id?: number | null;
      ai_agent_id?: number | null;
      clear_user?: boolean;
      clear_agent?: boolean;
    },
  ): Observable<{ ticket: SupportTicket }> {
    return this.http.post<{ ticket: SupportTicket }>(
      `${this.base}/tickets/${id}/assign`, payload,
    );
  }

  coordinateTicket(
    id: number,
    message: string,
    agentId?: number | null,
  ): Observable<{ reply: string; actions: any[]; agent_name?: string }> {
    return this.http.post<{ reply: string; actions: any[]; agent_name?: string }>(
      `${this.base}/tickets/${id}/coordinate`,
      { message, agent_id: agentId || undefined },
    );
  }

  coordinateEndpoint(id: number): string {
    return `${this.base}/tickets/${id}/coordinate`;
  }

  subscribeTicket(id: number, subscribed: boolean): Observable<{ ticket: SupportTicket }> {
    return this.http.post<{ ticket: SupportTicket }>(
      `${this.base}/tickets/${id}/subscribe`, { subscribed },
    );
  }

  createLinkedTask(id: number): Observable<{ ticket: SupportTicket }> {
    return this.http.post<{ ticket: SupportTicket }>(
      `${this.base}/tickets/${id}/task`, {},
    );
  }

  listTimesheets(id: number): Observable<{
    items: TicketTimesheet[]; total_minutes: number; billable_minutes: number;
  }> {
    return this.http.get<{
      items: TicketTimesheet[]; total_minutes: number; billable_minutes: number;
    }>(`${this.base}/tickets/${id}/timesheets`);
  }

  addTimesheet(
    id: number,
    payload: { minutes: number; note?: string },
  ): Observable<{ timesheet: TicketTimesheet }> {
    return this.http.post<{ timesheet: TicketTimesheet }>(
      `${this.base}/tickets/${id}/timesheets`, payload,
    );
  }

  getKpis(): Observable<SupportKpis> {
    return this.http.get<SupportKpis>(`${this.base}/kpis`);
  }

  // ── Grants d'accès support ──────────────────────────────────────
  listGrants(): Observable<{ items: SupportGrant[] }> {
    return this.http.get<{ items: SupportGrant[] }>(`${this.base}/grants`);
  }

  grantAccess(id: number, ttlHours?: number): Observable<{ grant?: SupportGrant }> {
    const body: Record<string, number> = {};
    if (ttlHours != null) body['ttl_hours'] = ttlHours;
    return this.http.post<{ grant?: SupportGrant }>(
      `${this.base}/grants/${id}/grant`, body,
    );
  }

  revokeAccess(id: number): Observable<{ grant?: SupportGrant }> {
    return this.http.post<{ grant?: SupportGrant }>(
      `${this.base}/grants/${id}/revoke`, {},
    );
  }
}
